import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  billingCurrency,
  diaYMes,
  diaYMesCorto,
  planEnPausa,
  PREFIJO_PLAN_EN_PAUSA,
  formatMoneyIn,
  fraseSinPlan,
  launchPricePerMonth,
  MENSAJE_PLAN_CANCELADO,
  MENSAJE_PRUEBA_TERMINADA,
  PLANS,
  planCancelado,
  sinPlanActivo,
} from '@alexiarest/shared';
import { useMe, useTrialSummary } from '../api/hooks';
import './finPrueba.css';

/**
 * Prueba terminada sin plan (sección 18 del lienzo, 15 oct 2026): la cuenta
 * VE TODO pero NO SUMA. Al tocar un botón de crear (subir factura, nuevo
 * plato, anotar el día, preguntar a Alexia…) sale «Para subir facturas, elige
 * un plan» en vez de abrir un formulario que luego no se va a poder guardar.
 *
 * Dos redes, para no tocar cada pantalla:
 * 1. Un clic en un botón de crear (por su texto o por `data-requiere-plan`)
 *    se para antes de llegar a la pantalla.
 * 2. Si algo se cuela y la API contesta «Tu prueba terminó», sale lo mismo.
 */

interface SinPlanApi {
  /** Abre el aviso con la frase de esa acción («Para subir facturas, elige un plan»). */
  pedirPlan: (frase?: string) => void;
  sinPlan: boolean;
}

const Ctx = createContext<SinPlanApi>({ pedirPlan: () => undefined, sinPlan: false });

export const useSinPlan = (): SinPlanApi => useContext(Ctx);

/** Botones que suman algo. Se mira el texto visible del botón o del enlace. */
const CREAR = /^(\+\s*)?(subir|escanear|nuevo|nueva|agregar|añadir|anotar|importar|dictar|crear|conectar|sincronizar|preguntar|cuéntaselo|cuentaselo|registrar|traer|juntar|duplicar|guardar las|confirmar precio)/i;
/** Pantallas donde se puede seguir tocando todo (configuración básica, elegir plan). */
const RUTAS_LIBRES = ['/ajustes', '/elegir-plan', '/prueba-extra'];

export function SinPlanProvider({ children }: { children: ReactNode }): JSX.Element {
  const me = useMe();
  const tenant = me.data?.tenant;
  // Prueba terminada sin plan o plan cancelado (se dio de baja): lo mismo.
  const sinPlan = sinPlanActivo(tenant);
  const [frase, setFrase] = useState<string | null>(null);
  const location = useLocation();
  const qc = useQueryClient();
  const ruta = useRef(location.pathname);
  ruta.current = location.pathname;

  const pedirPlan = useCallback((f?: string) => setFrase(f ?? fraseSinPlan(ruta.current)), []);

  // 1. Botones de crear: se paran en la fase de captura, antes que su onClick.
  useEffect(() => {
    if (!sinPlan) return undefined;
    const alPulsar = (event: MouseEvent) => {
      if (RUTAS_LIBRES.some((r) => ruta.current.startsWith(r))) return;
      const el = (event.target as HTMLElement | null)?.closest<HTMLElement>('button, a, [role="button"]');
      if (!el || el.closest('[data-sin-plan="libre"], .sin-plan-modal, .sidebar, .mobile-tabs, .mobile-bar, .topbar')) return;
      const marcado = el.closest<HTMLElement>('[data-requiere-plan]');
      const texto = (el.getAttribute('aria-label') || el.textContent || '').trim();
      if (!marcado && !CREAR.test(texto)) return;
      event.preventDefault();
      event.stopPropagation();
      setFrase(marcado?.dataset.requierePlan || fraseSinPlan(`${ruta.current} ${texto}`));
    };
    document.addEventListener('click', alPulsar, true);
    return () => document.removeEventListener('click', alPulsar, true);
  }, [sinPlan]);

  // 2. Lo que llegue a la API igualmente: el mismo aviso, no un error suelto.
  useEffect(() => {
    if (!sinPlan) return undefined;
    return qc.getMutationCache().subscribe((evento) => {
      const error = evento.mutation?.state.error as { code?: string; message?: string } | null | undefined;
      if (evento.type === 'updated' && error?.code === 'PLAN_LIMIT' && (error.message === MENSAJE_PRUEBA_TERMINADA || error.message === MENSAJE_PLAN_CANCELADO || error.message?.startsWith(PREFIJO_PLAN_EN_PAUSA) || /siete días extra/.test(error.message ?? ''))) {
        setFrase(fraseSinPlan(ruta.current));
      }
    });
  }, [qc, sinPlan]);

  const valor = useMemo(() => ({ pedirPlan, sinPlan }), [pedirPlan, sinPlan]);
  return (
    <Ctx.Provider value={valor}>
      {children}
      {sinPlan && frase && <AvisoSinPlan frase={frase} onCerrar={() => setFrase(null)} />}
    </Ctx.Provider>
  );
}

function AvisoSinPlan({ frase, onCerrar }: { frase: string; onCerrar: () => void }): JSX.Element {
  const me = useMe();
  const role = me.data?.user?.role;
  const tenant = me.data?.tenant;
  // Solo el dueño elige plan (ElegirPlanPage y la API); el admin, como el resto, se lo pide.
  const puedeElegir = role === 'owner';
  const resumen = useTrialSummary(puedeElegir);
  const cerrarRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cerrarRef.current?.focus();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onCerrar();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onCerrar]);
  const r = resumen.data;
  const plan = r?.recomendacion.plan ?? 'basic';
  // En pausa (retención, 18 oct 2026) no hay que elegir plan: hay que reanudarlo.
  const enPausa = planEnPausa(tenant);
  const titulo = enPausa ? frase.replace(/elige un plan$/, 'reanuda tu plan') : frase;
  const precio = formatMoneyIn(launchPricePerMonth(plan), billingCurrency(tenant?.country ?? 'ES'));
  const cosas = [
    r?.platosCosteados ? (r.platosCosteados === 1 ? 'tu plato' : `tus ${r.platosCosteados} platos`) : '',
    r?.facturas ? (r.facturas === 1 ? 'tu factura' : `tus ${r.facturas} facturas`) : '',
  ].filter(Boolean);
  return (
    <div className="sin-plan-modal" role="presentation" onClick={(e) => e.target === e.currentTarget && onCerrar()}>
      <div className="sin-plan-modal__caja" role="dialog" aria-modal="true" aria-labelledby="sin-plan-titulo">
        <h2 id="sin-plan-titulo" className="sin-plan-modal__titulo">{titulo}</h2>
        <p className="sin-plan-modal__texto">
          {enPausa ? (
            <>Tu plan está en pausa{tenant?.pausedUntil ? ` hasta el ${diaYMesCorto(tenant.pausedUntil)}` : ''}. Tus datos siguen aquí y puedes verlos y descargarlos.{' '}
              {puedeElegir ? 'Si quieres seguir sumando ya, reanuda tu plan.' : 'Pídele al dueño de la cuenta que reanude el plan.'}</>
          ) : planCancelado(tenant) ? (
            // Se dio de baja: no es «tu prueba terminó» (bugbot 15 oct 2026).
            <>Tu plan está cancelado.{' '}{puedeElegir
              ? <>Elige un plan para seguir guardando: con {PLANS[plan].name}, desde {precio} al mes, sigues donde lo dejaste.</>
              : 'Pídele al dueño de la cuenta que elija el plan para seguir guardando.'}</>
          ) : <>
          Tu prueba terminó{tenant?.trialEndsAt ? ` el ${diaYMes(tenant.trialEndsAt)}` : ''}.{' '}
          {puedeElegir
            ? <>Con {PLANS[plan].name}, desde {precio} al mes, sigues donde lo dejaste{cosas.length ? `: con ${cosas.join(' y ')}` : ''}.</>
            : 'Pídele al dueño de la cuenta que elija el plan para seguir sumando.'}
          </>}
        </p>
        <div className="sin-plan-modal__botones">
          {puedeElegir && (enPausa
            ? <Link to="/ajustes?tab=plan" className="btn" onClick={onCerrar}>Reanudar mi plan</Link>
            : <Link to="/elegir-plan" className="btn" onClick={onCerrar}>Elegir mi plan</Link>)}
          <button ref={cerrarRef} type="button" className="btn btn--ghost" onClick={onCerrar}>Ahora no</button>
        </div>
      </div>
    </div>
  );
}
